'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import Button from '../../atoms/Button';
import { Textarea } from '../../atoms/Textarea';
import { FormModal } from '../../molecules/FormModal/FormModal';
import {
  MAX_REPORT_REVIEW_CHARS,
  reportReviewFormSchema,
  type ReportReviewModalData,
  type ReportReviewModalProps,
} from './ReportReviewModal.types';

export const ReportReviewModal = ({
  reviewId,
  microcopy,
  onSubmitReport,
  className = '',
}: ReportReviewModalProps) => {
  const {
    control,
    handleSubmit,
    formState: { isValid, errors },
  } = useForm<{ reportReview: string }>({
    resolver: zodResolver(reportReviewFormSchema),
    mode: 'onChange',
    defaultValues: { reportReview: '' },
  });

  const onFormSubmit = (reportData: ReportReviewModalData) => {
    onSubmitReport(reviewId, reportData, true);
  };

  const triggerElement = (
    <Button.v2
      size="xsmall"
      shape="square"
      variant="tertiary-grey-monochrome"
      label={microcopy.triggerLabel}
      data-testid="report-review-button"
    />
  );

  const modalContent = (
    <div className="flex h-full flex-col pb-4">
      <Controller
        control={control}
        name="reportReview"
        render={({ field }) => (
          <Textarea
            data-testid="report-review-input"
            placeholder={microcopy.placeholder}
            value={field.value}
            onChange={(e) => field.onChange(e.target.value)}
            maxLength={MAX_REPORT_REVIEW_CHARS}
            showRemainingChars
            className="h-full min-h-24 w-full flex-1 resize-none overflow-auto rounded-lg border border-grey-300 px-3 py-2"
            aria-invalid={Boolean(errors.reportReview)}
          />
        )}
      />
    </div>
  );

  return (
    <FormModal
      size="small"
      trigger={triggerElement}
      content={modalContent}
      title={microcopy.title}
      subtitle={microcopy.subtitle}
      description={microcopy.description}
      descriptionClassName="leading-6 tracking-[-0.006em]"
      close={microcopy.close}
      submit={microcopy.primaryCta}
      disabled={!isValid}
      cancel={microcopy.secondaryCta}
      onSubmit={handleSubmit(onFormSubmit)}
      className={className}
    />
  );
};
